/**
 * Overview of all component library components.
 * Consult ./CLAUDE.md first for instructions.
 */

// ─── Displays ────────────────────────────────────────────────────────────────

/** Display an Ethereum account with a link to its account or explorer page */
export { AddressDisplay } from '~/components/component-library/AddressDisplay'

/** A small colored icon badge marking an address as a contact, ENS name, payee, internal account, etc. */
export { AddressDisplayBadge } from '~/components/component-library/AddressDisplay/AddressDisplayBadges'

/** Display a blockchain network by logo and name */
export { NetworkDisplay } from '~/components/component-library/NetworkDisplay'
export {
  type NetworkDisplayLayout,
  type NetworkDisplayProps,
} from '~/components/component-library/NetworkDisplay/types'

/** Display an ERC-20 token */
export {
  TokenDisplay,
  TokenDisplayView,
} from '~/components/component-library/TokenDisplay'
export {
  type TokenDisplayProps,
  type TokenDisplayViewProps,
} from '~/components/component-library/TokenDisplay/types'

/** Display a user or person */
export { UserDisplay } from '~/components/component-library/UserDisplay'

// ─── Balances ────────────────────────────────────────────────────────────────

/** A properly formatted amount of fiat currency */
export { CurrencyAmount } from '~/components/component-library/CurrencyAmount'
export { type CurrencyAmountProps } from '~/components/component-library/CurrencyAmount/types'

/** A balance of a token along with its fiat value */
export {
  TokenBalance,
  TokenBalanceContainer,
  TokenBalanceView,
  tokenBalanceFormatters,
  type TokenBalanceProps,
} from '~/components/component-library/TokenBalance'

/** A horizontal bar split into colored segments by value, with a hover breakdown of each segment's assets */
export {
  SegmentedAssetsBar,
  type Segment,
} from '~/components/component-library/SegmentedAssetsBar'

// ─── Layouts ─────────────────────────────────────────────────────────────────

/** Standard page wrapper that all pages in our app should use */
export { PageLayout } from '~/components/component-library/PageLayout/index'
export { type PageLayoutProps } from '~/components/component-library/PageLayout/types'

/** A vertical list of similarly-typed items */
export { List } from '~/components/component-library/List'
export type {
  ListProps,
  ListItemProps,
} from '~/components/component-library/List/types'

/** A labeled section around a group of items, with an optional description, secondary action, and footer */
export { Group, type GroupProps } from '~/components/component-library/Group'
/** An icon-only action for the secondaryDisplay of a Group or List header */
export {
  GroupIconAction,
  type GroupIconActionProps,
} from '~/components/component-library/Group'

/** A label and value pair, mainly for details and metadata */
export { LabelValue } from '~/components/component-library/LabelValue'
export type { LabelValueProps } from '~/components/component-library/LabelValue/types'

/** A navigation control that switches between sibling views; compose TabsRoot + TabsList/TabsTrigger + TabsContent when the list and its panels live in different slots (e.g. PageLayout stickyContent); below md the list collapses to a dropdown, except the dense variant */
export {
  Tabs,
  TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
  type TabOption,
  type TabsListVariant,
} from '~/components/component-library/Tabs'

/** A trail of breadcrumbs that shows the current page's hierarchy */
export { Breadcrumb } from '~/components/component-library/Breadcrumb'
export type {
  BreadcrumbProps,
  BreadcrumbItem,
} from '~/components/component-library/Breadcrumb/types'

/** A section of content that can be expanded or collapsed */
export {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '~/components/component-library/Collapsible'

// ─── Inputs ──────────────────────────────────────────────────────────────────

/** A single-line text field */
export {
  TextInput,
  type TextInputProps,
} from '~/components/component-library/TextInput'

/** A multi-line text field */
export {
  TextAreaInput,
  type TextAreaInputProps,
} from '~/components/component-library/TextAreaInput'

/** A numeric field with thousands/decimal formatting and validation */
export {
  NumberInput,
  buildNumberFormatIsAllowed,
  type NumberInputProps,
} from '~/components/component-library/NumberInput'

/** A search field that resolves addresses, ENS, and contacts, with a dropdown of results */
export {
  AddressSearchInput,
  type AddressSearchInputProps,
} from '~/components/component-library/AddressSearchInput'

/** An address field with ENS resolution and validation */
export {
  AddressInput,
  type AddressInputProps,
} from '~/components/component-library/AddressInput'

/** A group of checkboxes that allows users to select multiple options */
export {
  CheckboxGroupInput,
  type CheckboxGroupInputProps,
} from '~/components/component-library/CheckboxGroupInput'

/** A group of radio buttons for choosing a single option */
export {
  RadioGroupInput,
  type RadioGroupInputProps,
} from '~/components/component-library/RadioGroupInput'

/** A control that allows users to pick one option from a set of inline buttons */
export { SegmentedControl } from '~/components/component-library/SegmentedControl'
export type { SegmentedControlProps } from '~/components/component-library/SegmentedControl/types'

/** A popover that allows users to choose one option from a list */
export {
  SelectInput,
  type SelectInputProps,
} from '~/components/component-library/SelectInput'

/** A searchable select: type to filter a long list, pick one value or several (as chips) */
export {
  ComboboxInput,
  type ComboboxInputProps,
} from '~/components/component-library/ComboboxInput'

/** A popover that allows users to choose one of the org's smart accounts */
export {
  SmartAccountSelectInput,
  type SmartAccountSelectInputProps,
} from '~/components/component-library/SmartAccountSelectInput'

/** A date picker with a calendar dropdown */
export { DateInput } from '~/components/component-library/DateInput'
export type { DateInputProps } from '~/components/component-library/DateInput/types'

/** A popover that allows users to search and pick a token, and optionally amount */
export { TokenSelector } from '~/components/component-library/TokenSelector'
/** The token-picker popover behind a standalone trigger button, for picking a token without an amount input */
export { TokenSelectorTriggerOnly } from '~/components/component-library/TokenSelector/TokenSelectorTriggerOnly'
export { type SelectedToken } from '~/components/component-library/TokenSelector/useSelectedAggregatedBalance'
export {
  type TokenSelectorProps,
  type Denomination,
} from '~/components/component-library/TokenSelector/types'

// ─── Buttons ─────────────────────────────────────────────────────────────────

/** Primary action control with variants and sizes */
export { Button } from '~/components/component-library/Button'
export type {
  ButtonProps,
  ButtonDropdownOption,
} from '~/components/component-library/Button/types'

/** A formatted container that allows users to copy its content on click */
export {
  Copyable,
  type CopyableProps,
  type CopyableLayout,
} from '~/components/component-library/Copyable'

/** A filter's open control with a separate clear control beside it, for use inside a Popover */
export {
  FilterTrigger,
  type FilterTriggerProps,
} from '~/components/component-library/FilterTrigger'

/** A labeled row that toggles a setting on or off */
export {
  Toggleable,
  type ToggleableProps,
  type ToggleableLayout,
} from '~/components/component-library/Toggleable'

// ─── Overlays ────────────────────────────────────────────────────────────────

/** The content panel of a dialog, with a title and standard action, cancel, and delete buttons */
export { SplitsDialogContent } from '~/components/component-library/Dialog'

/** A heading with a divider for a section inside a dialog */
export { DialogSectionHeader } from '~/components/component-library/Dialog/DialogSectionHeader'

/** A menu of actions anchored to a trigger */
export { Dropdown } from '~/components/component-library/Dropdown'
export type {
  DropdownProps,
  DropdownItem,
  DropdownEntry,
  DropdownItemHref,
} from '~/components/component-library/Dropdown/types'

/**
 * A hover or focus label that gives added (but non-blocking) context.
 * `TooltipLabel` (title with detail lines) and `TooltipList` (caption above
 * rows) are its standard content layouts.
 */
export {
  Tooltip,
  TooltipLabel,
  TooltipList,
} from '~/components/component-library/Tooltip'
export type {
  TooltipLabelProps,
  TooltipListProps,
  TooltipProps,
} from '~/components/component-library/Tooltip'

// ─── Feedback ────────────────────────────────────────────────────────────────

/** An inline info, warning, or error callout */
export {
  Message,
  type MessageAction,
  type MessageProps,
} from '~/components/component-library/Message'

/** A spinner that indicates an in-progress operation */
export { LoadingSpinner } from '~/components/component-library/LoadingSpinner'

/** A placeholder shape that is shown while content loads */
export { Skeleton } from '~/components/component-library/Skeleton'

/** A placeholder for missing data or failed lookups, with compact, default and page sizes and a separate heading level */
export { EmptyState } from '~/components/component-library/EmptyState'

// ─── Decorations ─────────────────────────────────────────────────────────────

/** A small rounded label for a tag or status */
export { Pill } from '~/components/component-library/Pill'

/** A logo for one network, or a stack of logos for several. For a logo with its name, use NetworkDisplay */
export {
  NetworkLogo,
  NetworkLogoView,
} from '~/components/component-library/NetworkLogo'
export {
  type NetworkLogoProps,
  type NetworkLogoViewProps,
} from '~/components/component-library/NetworkLogo/types'

/** A single on/off checkbox */
export {
  Checkbox,
  type CheckboxProps,
} from '~/components/component-library/Checkbox'

/** A control that toggles between on and off states */
export { Toggle, type ToggleProps } from '~/components/component-library/Toggle'

// ─── Other ───────────────────────────────────────────────────────────────────

/** A date/time as relative or absolute text */
export { Timestamp } from '~/components/component-library/Timestamp'

/** A number that animates when its value changes */
export { AnimatedNumber } from '~/components/component-library/AnimatedNumber'

/** A transaction's title rendered from its title chunks (amounts, tokens, recipients) */
export { TransactionTitle } from '~/components/component-library/TransactionTitle'

// ─── Shared building blocks ──────────────────────────────────────────────────

/** Wraps a disabled control with a tooltip explaining why it's unavailable */
export {
  DisabledReasonTooltip,
  DISABLED_INPUT_CLASSNAME,
} from '~/components/component-library/internal/DisabledReasonTooltip'

/** Two stacked lines (primary over secondary) at the CL's standard two-line line heights */
export { TwoLineFragment } from '~/components/component-library/internal/Fragments'
export type { TwoLineFragmentProps } from '~/components/component-library/internal/Fragments/types'

export type {
  /** Text or a rendered element */
  CLRenderable,
  /** Standard size variants */
  CLSize,
  /** Standard layout variants */
  CLLayout,
  /** Standard type for clickable links */
  CLHref,
  /** Option shape for SelectInput */
  SelectOption,
} from '~/components/component-library/types'

// Table, Toast and StateChangeList are documented on the CL site but imported
// from elsewhere; see this directory's CLAUDE.md.
